import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  buildDuctedCombinationsFetchParams,
  deriveDuctedRecommendedSummary,
  ductedCoolingOperatingCostParamsFromProject,
  ductedKeptComponentIds,
  fetchDuctedCompatibleCombinations,
  recordDuctedCombinationsSkipped,
  resultSetsFor,
  summaryPending
} from 'features/ducted-compatibility';
import {
  isTonnageSeedApplied,
  tonnageSeedKeyOf
} from 'features/ui/tonnage-filter-seed';
import useDuctedFilterContextReset from 'hooks/use-ducted-filter-context-reset';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import { useEffect, useMemo, useRef } from 'react';
import {
  ductedPairLoadsFor,
  ductedPairLoadsKey,
  selectDuctedPairContextBySystemId
} from 'utils/ducted-pair-context';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';
import { buildComponentsKey } from 'utils/recommendations-strip-logic';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import { resolveStructureTonnageSeed } from 'utils/tonnage-seed-state';
import { boundedCodesFor, scopedDuctedTypeCodes } from './ducted-facet-merge';

// Page-level fetch orchestration for the Choose Systems ducted filter
// section (AHA-6681 CS-1, wants-gateway since sub-build 3): one
// compatible-combinations POST per eligible ducted system and result set,
// driven by the shared ductedFilters state. That one POST also carries the
// rail's Recommended row per system type (the thunk fills the per-type
// representative entries from it), so there is no second request per set.
// The wants selection is THE gateway — a system without wants (or without a
// resolvable recommended bound) never fetches: it records the honest skip
// and the rail renders the steering empty state. Mounted from
// EquipmentComparisonView, independent of the section render, so CS-2's
// middle column can read the same slice.
export default function useDuctedCompatibilityFetch() {
  const dispatch = useAppDispatch();
  const isDucted = useIsCompatibilityPathway();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const compatibleResultsBySystem = useAppSelector(
    (state) => state.structure.compatibleResultsBySystem
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const recommendationSelectionsLoaded = useAppSelector(
    (state) => state.structure.recommendationSelectionsLoaded
  );
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  // The "Sort by" selection: the server pages the whole pool in this order,
  // so a dropdown change refetches page one (the refreshing skeletons cover
  // the wait).
  const systemSort = useAppSelector((state) => state.ui.systemSort);
  const inStockOnly = useAppSelector(
    (state) => state.ui.galleryFilters.inStock
  );
  const priceMin = useAppSelector((state) => state.ui.galleryFilters.priceMin);
  const priceMax = useAppSelector((state) => state.ui.galleryFilters.priceMax);
  const locationData = useAppSelector((state) => state.project.locationData);
  const totalBTUs = useAppSelector((state) => state.project.totalBTUs);
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );

  // Per system: the block loads of the System Design pair linked to it when
  // one exists, else the project's — if a sizing calc produced any,
  // otherwise nothing but location and thermostat. The load a home without
  // a calc gets measured against is the server's to resolve, off the
  // per-system tonnageSeed below.
  const pairContexts = useAppSelector(selectDuctedPairContextBySystemId);
  const pairLoadsKey = ductedPairLoadsKey(pairContexts);
  const loadsFor = (systemUniqueId: string) =>
    ductedPairLoadsFor(pairContexts, systemUniqueId, totalBTUs);
  const coolingOperatingCostFor = (systemUniqueId: string) =>
    ductedCoolingOperatingCostParamsFromProject({
      locationData,
      totalBTUs: loadsFor(systemUniqueId),
      projectSettings
    });

  // Once-per-inputs guard for the missing-summary derivation (sub-build 3):
  // the derivation runs async before any summary entry exists, so effect
  // re-runs in that window (chip clicks, StrictMode double-invoke) must not
  // stampede the context/recommendations endpoints. Keyed on everything the
  // derivation consumes; a failure stays recorded as the honest skip until
  // the inputs change.
  const derivationDispatchedRef = useRef<Set<string>>(new Set());

  // Eligibility includes zero-component systems with a completed strip
  // (virtual-tile haves) — they carry no kept set, so only the alternatives
  // fetch is in play and they pass the wants gateway by construction.
  const eligibleSystems = useMemo(
    () =>
      isDucted
        ? getChooseSystemsEligibleDuctedSystems(
            existingSystems,
            recommendationSelectionsBySystem,
            wantCompositionPicksBySystem
          )
        : [],
    [
      isDucted,
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );

  // Stable dep key for "the eligible component sets changed": structure
  // edits churn the existingSystems reference without changing the fetch
  // inputs, so the effect keys on this string instead of the array.
  const componentsKey = useMemo(
    () =>
      eligibleSystems
        .map(
          (system) =>
            `${system.uniqueId}=${buildComponentsKey(system.components)}`
        )
        .join(';'),
    [eligibleSystems]
  );

  // The once-per-results-context ductedFilters reset lives in the SHARED
  // hook now (AHA-6796 EE rows — the Existing Equipment page mounts it
  // too, so the seed lands before the user ever reaches Choose Systems);
  // this hook only gates its fetches on the applied reset.
  const { resetApplied } = useDuctedFilterContextReset();

  const structureTonnageSeedKey = useAppSelector((state) =>
    tonnageSeedKeyOf(resolveStructureTonnageSeed(state))
  );
  const tonnageSeedApplied = isTonnageSeedApplied(
    ductedFilters,
    structureTonnageSeedKey
  );

  // Content-keyed dep for the per-system recommended bounds (AHA-6681
  // requirement D): '*' = no summary (bound unresolvable — derivation
  // pending or dead-ended). Same pattern as componentsKey/resultsKey — the
  // summaries live on entries whose references churn on unrelated store
  // writes, so the effects key on this string. The pending marker makes the
  // loading→settled transition of an unchanged code list (e.g. a derived
  // empty summary) refire the effect so the honest skip lands.
  const boundedCodesKey = useMemo(
    () =>
      eligibleSystems
        .map((system) => {
          const summary = compatibleResultsBySystem[system.uniqueId];
          const bounded = boundedCodesFor(summary);
          return `${system.uniqueId}:${
            bounded === null ? '*' : bounded.join(',')
          }:${summaryPending(summary) ? 'pending' : 'settled'}`;
        })
        .join(';'),
    [eligibleSystems, compatibleResultsBySystem]
  );

  // Content-keyed dep for the per-system wants (the gateway): hydration
  // landing or a selection change must re-evaluate the skip/derive/fetch
  // decision.
  const wantsKey = useMemo(
    () =>
      eligibleSystems
        .map(
          (system) =>
            `${system.uniqueId}:${(
              recommendationSelectionsBySystem[system.uniqueId]?.wantIds ?? []
            ).join(',')}`
        )
        .join(';'),
    [eligibleSystems, recommendationSelectionsBySystem]
  );

  // Content-keyed dep for the per-system SYSTEM SIZE records: the tonnage
  // record feeds the cooling-load fallback in the operating-cost request
  // fields AND the seed the server sizes coverage against, so a record
  // change must refire the fetches (same reference-churn reasoning as
  // wantsKey). The dismissal flag rides along because it alone can turn a
  // resolved seed into none.
  const tonnageKey = useMemo(
    () =>
      eligibleSystems
        .map((system) => {
          const record =
            recommendationSelectionsBySystem[system.uniqueId]?.tonnage;
          return `${system.uniqueId}:${
            record
              ? `${record.source}=${record.tons.join(',')}/${
                  record.filterDismissed ? 'dismissed' : 'active'
                }`
              : '*'
          }`;
        })
        .join(';'),
    [eligibleSystems, recommendationSelectionsBySystem]
  );

  useEffect(() => {
    // Ducted-path gating (hard constraint): zero effect work — and zero
    // fetches — outside the ducted pathway.
    if (!isDucted || eligibleSystems.length === 0) return;

    // Wants unknown until the structure-load hydration lands: deciding
    // skip-vs-fetch on the unhydrated mirror would flash a false empty
    // state, so do nothing until it flips.
    if (!recommendationSelectionsLoaded) return;

    // Wait out the context reset (dispatched by the shared
    // useDuctedFilterContextReset hook above) so a stale seed-sourced
    // selection never rides into the first fetch for a new results
    // context; the effect re-runs once the reset lands (resetApplied is a
    // dep). User edits stick within a context — and manual fuel/chassis
    // survive even a context change (AHA-6796 EE rows). The default
    // selection is empty = ALL RECOMMENDED (the All-Types browse-everything
    // default is gone since sub-build 3).
    if (!resetApplied) return;
    if (!tonnageSeedApplied) return;

    eligibleSystems.forEach((system) => {
      // Kept = components NOT being replaced (ductedKeptComponentIds).
      // Determines which result sets are in play (sub-build 4): the
      // kept-constrained fetch is skipped entirely when zero components are
      // kept.
      const keptComponentUniqueIds = ductedKeptComponentIds(system);
      const resultSets = resultSetsFor(keptComponentUniqueIds);

      // The wants gateway (sub-build 3): no wants ⇒ nothing recommended ⇒
      // nothing to fetch — record the honest skip; the rail's empty state
      // steers back to the Existing Equipment step.
      const wantIds =
        recommendationSelectionsBySystem[system.uniqueId]?.wantIds ?? [];
      if (wantIds.length === 0) {
        resultSets.forEach((resultSet) => {
          dispatch(
            recordDuctedCombinationsSkipped({
              systemUniqueId: system.uniqueId,
              resultSet
            })
          );
        });
        return;
      }

      const summary = compatibleResultsBySystem[system.uniqueId];
      const bounded = boundedCodesFor(summary);

      // No summary (fresh hydration / old rows): the bound is unresolvable
      // — derive it via the recommendations POST before fetching (sub-build
      // 3 fallback). The effect re-runs when the derived summary lands.
      if (bounded === null) {
        const derivationKey = `${system.uniqueId}|${buildComponentsKey(
          system.components
        )}|${wantIds.join(',')}`;
        if (!derivationDispatchedRef.current.has(derivationKey)) {
          derivationDispatchedRef.current.add(derivationKey);
          dispatch(
            deriveDuctedRecommendedSummary({
              systemUniqueId: system.uniqueId,
              keptComponentUniqueIds,
              componentsKey: buildComponentsKey(system.components)
            })
          );
        }
        return;
      }

      // A first check/derivation is still in flight: its transient empty
      // code list is not a bound — wait for the settle.
      if (summaryPending(summary)) return;

      // Bounded to NOTHING (zero-result recommended summary): this system
      // contributes no results — record the honest skip instead of fetching
      // everything ([] would read as an unbounded search server-side).
      if (bounded.length === 0) {
        resultSets.forEach((resultSet) => {
          dispatch(
            recordDuctedCombinationsSkipped({
              systemUniqueId: system.uniqueId,
              resultSet
            })
          );
        });
        return;
      }

      // Up to two fetches per system (sub-build 4): the kept-constrained
      // set (only when ≥1 kept component) and the full-alternatives set
      // (the thunk sends keptComponentUniqueIds: [] for it). Both carry
      // the same recommended universe. Params come from the SHARED builder
      // (sub-build 5) — the pre-warm builds through the same one, so its
      // requestBodyHash is byte-identical to this first fetch and the
      // thunk's in-flight short-circuit attaches instead of duplicating.
      resultSets.forEach((resultSet) => {
        dispatch(
          fetchDuctedCompatibleCombinations(
            buildDuctedCombinationsFetchParams({
              systemUniqueId: system.uniqueId,
              resultSet,
              keptComponentUniqueIds,
              // The full recommended set, independent of the chip
              // narrowing: scopes the pnet cache universe (sub-build 3
              // plumbing). Chips narrow WITHIN it (requirement D.1 —
              // never widens past the bound), scoped to THIS result set
              // (AHA-6681 filter split).
              boundedCodes: bounded,
              chipCodes: scopedDuctedTypeCodes(
                ductedFilters.systemTypeSelections,
                resultSet
              ).codes,
              // The Fuel chips narrow the bound before the chip resolution
              // (AHA-6796 — see the builder's composition contract).
              fuelSelections: ductedFilters.fuelSelections,
              filters: ductedFilters.filters,
              inStockOnly,
              priceMin,
              priceMax,
              pageNumber: ductedFilters.pageNumber,
              pageSize: ductedFilters.pageSize,
              coolingOperatingCost: coolingOperatingCostFor(system.uniqueId),
              // Sizing-bands relax override (AHA-6796 T3): toggling it
              // re-runs this effect (ductedFilters is a dep) and the new
              // body hash busts the thunk dedupe naturally.
              applySizingBands: ductedFilters.applySizingBands,
              tonnageSeed: resolveSystemTonnageSeed({
                system,
                record:
                  recommendationSelectionsBySystem[system.uniqueId]?.tonnage ??
                  null,
                coolingBtu: loadsFor(system.uniqueId).cooling
              }),
              // The page order is the server's order under this: a
              // dropdown change re-runs this effect and the new body hash
              // refetches page one in the new order.
              ductedSort: systemSort
            })
          )
        );
      });
    });
    // eligibleSystems/compatibleResultsBySystem/
    // recommendationSelectionsBySystem/pairContexts are derived from the
    // componentsKey/boundedCodesKey/wantsKey/pairLoadsKey dependencies —
    // keying on the strings avoids refires from reference churn with
    // unchanged content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDucted,
    componentsKey,
    resetApplied,
    tonnageSeedApplied,
    boundedCodesKey,
    wantsKey,
    tonnageKey,
    pairLoadsKey,
    recommendationSelectionsLoaded,
    ductedFilters,
    inStockOnly,
    priceMin,
    priceMax,
    locationData,
    totalBTUs,
    projectSettings,
    systemSort,
    dispatch
  ]);
}
